import type { ReactNode } from 'react'
import { useResource } from '../../../shared/hooks/useResource'
import { AlertIcon, CheckIcon, InfoIcon, StatusPill, TrendUpIcon } from '../../../shared/components'
import {
  getStrengths,
  type SkillStanding,
  type StaffAuth,
  type Standing,
  type StrengthsReport,
} from '../../../shared/api/teaching'
import { DataSurface } from '../../../shared/components'
import { describeError, type Lang } from './copy'
import { SKILL_LABEL, STUDENT_COPY } from './studentCopy'

/**
 * Where this child is strong and where they are stuck.
 *
 * 🔴 IT REPLACES A PANEL THAT WAS ANSWERING A DIFFERENT QUESTION. D5's "insight"
 * ranks a child's skills by how many EVENTS they produced and prints the top two
 * under the word *"Strengths"* — so a child who spent the week failing Letter
 * Lagoon is described to their teacher as strong at literacy. Activity is not
 * attainment. That panel stays (it is a fair summary of where the time went) and
 * this one answers the question the teacher actually opened the page for.
 *
 * 🔴 EVERY ROW SHOWS ITS EVIDENCE, AND THE FRACTION IS THE EVIDENCE. "14 right of
 * 20 answered" is checkable against the child in front of her; "70%" is a grade.
 * The class figure sits beside it as a percentage precisely because it is a
 * comparison rather than a count — and it is only ever drawn when there were
 * enough classmates' answers to mean something.
 *
 * ⚠️ TWO OF THE FIVE STANDINGS ARE REFUSALS, AND THEY ARE THE MOST IMPORTANT
 * ONES ON A FRESH CLASS. "Not started" and "too early to say" keep the panel
 * from opening as a list of five gaps on the first morning of a pilot, which is
 * how a panel teaches a teacher to ignore it.
 */

/** The tone each standing is allowed. ⚠️ No red anywhere — see below. */
const TONE: Record<Standing, 'success' | 'warning' | 'neutral'> = {
  STRONG: 'success',
  STEADY: 'success',
  /*
   * ⚠️ WARNING, NEVER DANGER. `AttendanceScreen` on the parent tier refuses
   * `danger` on a child's count for a stated reason — *"a fact about a term, not
   * an alarm about a family"* — and a skill a five-year-old has not got to yet is
   * further from an alarm than that. It is also a fact about a SKILL in a period,
   * not a label for a person.
   */
  NEEDS_PRACTICE: 'warning',
  NOT_ENOUGH_YET: 'neutral',
  NOT_STARTED: 'neutral',
}

/**
 * 🔴 §12 — ICON **AND** COLOUR **AND** TEXT, ALL THREE, which `StatusPill`'s own
 * type requires rather than merely recommends. Two of these standings share a
 * tone (`STRONG` and `STEADY` are both `success`) and two share the neutral one,
 * so without a distinct glyph the pill would be carrying the difference in a
 * word alone at 13px — and the two neutral ones mean opposite things to a
 * teacher: one is "not enough evidence", the other is "go and start them".
 */
const ICON: Record<Standing, ReactNode> = {
  STRONG: <TrendUpIcon size={14} />,
  STEADY: <CheckIcon size={14} />,
  NEEDS_PRACTICE: <AlertIcon size={14} />,
  NOT_ENOUGH_YET: <InfoIcon size={14} />,
  NOT_STARTED: <InfoIcon size={14} />,
}

function standingLabel(standing: Standing, lang: Lang): string {
  const s = STUDENT_COPY[lang]
  switch (standing) {
    case 'STRONG':
      return s.standingStrong
    case 'STEADY':
      return s.standingSteady
    case 'NEEDS_PRACTICE':
      return s.standingNeedsPractice
    case 'NOT_ENOUGH_YET':
      return s.standingNotEnough
    default:
      return s.standingNotStarted
  }
}

/**
 * 🔴 THE ORDER IS THE POINT OF THE PANEL. A teacher reads the top and stops, so
 * what is at the top had better be the skill she can do something about today.
 * Gaps first, then the untouched ones (hand them the tablet), then what is going
 * well — which she needs to see but does not need to act on.
 */
const RANK: Record<Standing, number> = {
  NEEDS_PRACTICE: 0,
  NOT_STARTED: 1,
  NOT_ENOUGH_YET: 2,
  STEADY: 3,
  STRONG: 4,
}

export function StrengthsPanel({
  playerId,
  lang = 'en',
  auth,
}: {
  playerId: string
  lang?: Lang
  auth?: StaffAuth
}) {
  const s = STUDENT_COPY[lang]
  const resource = useResource<StrengthsReport>(
    (signal) => getStrengths(playerId, { ...auth, signal }),
    ['strengths', playerId],
  )

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <h2 className="mb-1 font-dash text-dash-md font-extrabold text-ink">{s.strengthsTitle}</h2>
      <p className="mb-4 text-dash-xs text-muted">{s.strengthsWhy}</p>

      <DataSurface
        state={resource.state}
        skeletonRows={4}
        emptyMessage={s.strengthsEmpty}
        errorMessage={describeError(resource.error, lang)}
        errorRetryLabel={STUDENT_COPY[lang].saving}
        onRetry={resource.retry}
      >
        {resource.data === null ? null : (
          <>
            <ul className="flex flex-col gap-3">
              {[...resource.data.skills]
                .sort(
                  (a, z) => RANK[a.standing] - RANK[z.standing] || a.skill.localeCompare(z.skill),
                )
                .map((row) => (
                  <StandingRow key={row.skill} row={row} lang={lang} />
                ))}
            </ul>
            {/* 🔴 WHAT THE COMPARISON WAS DRAWN FROM. A verdict whose population
                is invisible is a verdict a teacher cannot argue with — and on a
                class of three she should. */}
            <p className="mt-4 text-dash-xs text-muted">{s.strengthsPeers(resource.data.peers)}</p>
          </>
        )}
      </DataSurface>
    </div>
  )
}

function StandingRow({ row, lang }: { row: SkillStanding; lang: Lang }) {
  const s = STUDENT_COPY[lang]

  return (
    <li className="flex flex-col gap-1.5 border-b border-line pb-3 last:border-0 last:pb-0">
      <span className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-dash text-dash-sm font-bold text-ink">
          {SKILL_LABEL[row.skill]?.[lang] ?? row.skill}
        </span>
        <StatusPill tone={TONE[row.standing]} icon={ICON[row.standing]}>
          {standingLabel(row.standing, lang)}
        </StatusPill>
      </span>

      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 font-dash text-dash-xs text-muted">
        {row.standing === 'NOT_STARTED' ? (
          <span>{s.strengthsNotStartedHint}</span>
        ) : row.accuracy === null ? (
          <span>{s.strengthsNotEnoughHint(row.attempts)}</span>
        ) : (
          <>
            {/* 🔴 THE FRACTION, NOT THE PERCENTAGE. OD-39's rule, and the thing
                that makes this checkable against the child in front of her. */}
            <span className="tabular">{s.strengthsAccuracy(row.correct, row.attempts)}</span>
            {row.classAccuracy !== null && (
              <span className="tabular">
                {s.strengthsClass(Math.round(row.classAccuracy * 100))}
              </span>
            )}
            {row.topLevel !== null && (
              <span className="tabular">{s.strengthsLevel(row.topLevel)}</span>
            )}
          </>
        )}
      </span>

      {/*
        🔴 THE LINE NOTHING IN THIS PRODUCT HAS EVER SHOWN ANYBODY. `play.prisma`:
        *"Ceiling advancement LOOKS IDENTICAL TO SUCCESS in every accuracy
        average, so the ceiling count is the only signal that a level's content is
        too hard."* It is drawn regardless of the standing, because the case worth
        catching is the child whose average looks fine.
      */}
      {row.ceiling > 0 && (
        <span className="font-dash text-dash-xs" style={{ color: 'var(--color-warning)' }}>
          {s.strengthsCeiling(row.ceiling)}
        </span>
      )}
    </li>
  )
}
